import React, { useEffect, useState, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { useMonth } from '../contexts/MonthContext';
import { useBudgetData } from '../hooks/useBudgetData';
import { useTransactionMutations } from '../hooks/useTransactionMutations';
import { useCategoryMutations } from '../hooks/useCategoryMutations';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Label from '../components/ui/Label';
import NumberInput from '../components/ui/NumberInput';
import Toast from '../components/ui/Toast';
import SimpleMobileMode from '../components/budget/SimpleMobileMode';
import BudgetTable from '../components/budget/BudgetTable';
import CategoryBottomSheet from '../components/budget/CategoryBottomSheet';
import SortableCategoryRow from '../components/budget/SortableCategoryRow';
import RecurringReviewSheet from '../components/budget/RecurringReviewSheet';
import OnboardingModal from '../components/budget/OnboardingModal';
import { formatCurrency, seedDefaultCategories, getCurrencySymbol, getProgressColor } from '../lib/utils';
import { Plus, X, Upload, Copy, ChevronDown, RefreshCw } from 'lucide-react';
import { cn } from '../lib/utils';
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';

const POINTER_SENSOR_OPTIONS = { activationConstraint: { distance: 8 } };
const TOUCH_SENSOR_OPTIONS = { activationConstraint: { delay: 200, tolerance: 8 } };

const categoryTemplates = [
  { name: 'Groceries', budget: 500 },
  { name: 'Dining Out', budget: 250 },
  { name: 'Transportation', budget: 300 },
  { name: 'Entertainment', budget: 150 },
  { name: 'Shopping', budget: 200 },
  { name: 'Utilities', budget: 200 },
  { name: 'Healthcare', budget: 100 },
];

const Budget = () => {
  const { user } = useAuth();
  const { currentMonth } = useMonth();

  const [toast, setToast] = useState(null);

  const {
    categoryGroups, setCategoryGroups,
    categories, setCategories,
    transactions, setTransactions,
    monthlyIncome, setMonthlyIncome,
    allocations, setAllocations,
    loading,
    pendingRecurring, setPendingRecurring,
    expandedGroups, setExpandedGroups, toggleGroup,
    totalBudgeted, totalSpent, available,
    transactionsByCategory,
    refreshData,
  } = useBudgetData(user, currentMonth, setToast);

  const [lastUsedCategory, setLastUsedCategory] = useState(null);

  const {
    pendingDelete, setPendingDelete,
    syncTxns,
    getDefaultTransactionDate,
    addTransaction,
    simpleModeExpense,
    deleteTransaction,
    confirmDelete,
    confirmRecurring,
    denyRecurring,
    confirmAllRecurring,
    importCSV,
  } = useTransactionMutations({
    user, currentMonth, categories, setTransactions,
    pendingRecurring, setPendingRecurring,
    setLastUsedCategory, setToast,
  });

  const {
    addCategory,
    addGroup,
    updateBudgetInline,
    deleteGroup,
    deleteCategory,
    renameCategory,
    renameGroup,
    saveMonthlyIncome,
    copyPreviousMonth,
    handleDragEnd,
  } = useCategoryMutations({
    user, currentMonth,
    categories, setCategories,
    categoryGroups, setCategoryGroups,
    allocations, setAllocations,
    setMonthlyIncome,
    setExpandedGroups,
    setToast,
    refreshData,
  });

  // Keep txnsRef in sync for deleteTransaction lookup
  useEffect(() => { syncTxns(transactions); }, [transactions, syncTxns]);

  // Auto-close recurring sheet when all items are resolved
  useEffect(() => {
    if (pendingRecurring.length === 0) setShowRecurringSheet(false);
  }, [pendingRecurring.length]);

  // Modal / UI state
  const [showAddTransaction, setShowAddTransaction] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showCopyConfirm, setShowCopyConfirm] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showRecurringSheet, setShowRecurringSheet] = useState(false);
  const [editingIncome, setEditingIncome] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [showCategorySheet, setShowCategorySheet] = useState(false);
  const [selectedCategoryForSheet, setSelectedCategoryForSheet] = useState(null);
  const [editingGroup, setEditingGroup] = useState(null);
  const [editingBudget, setEditingBudget] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [currencySymbol, setCurrencySymbol] = useState('£');
  const [simpleMode, setSimpleMode] = useState(() => {
    const saved = localStorage.getItem('simpleMode');
    if (saved !== null) return saved === 'true';
    return false;
  });

  const [transactionForm, setTransactionForm] = useState({
    category_id: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    description: '',
    is_recurring: false,
  });
  const [categoryForm, setCategoryForm] = useState({ name: '', budgeted_amount: '', group_id: '' });
  const [groupForm, setGroupForm] = useState({ name: '' });

  const budgetInputRef = useRef(null);

  // Seed default categories once on mount
  useEffect(() => {
    if (user?.id) seedDefaultCategories(supabase, user.id);
  }, [user?.id]);

  // Fetch data with abort controller to prevent stale updates on fast month switches
  useEffect(() => {
    if (!user) return;
    const controller = new AbortController();
    refreshData(controller.signal);
    return () => controller.abort();
  }, [user, currentMonth, refreshData]);

  useEffect(() => {
    if (categories.length > 0) {
      const lastCategoryId = localStorage.getItem('lastUsedCategoryId');
      if (lastCategoryId) {
        const cat = categories.find(c => c.id === lastCategoryId);
        if (cat) setLastUsedCategory(cat);
      }
    }
  }, [categories]);

  useEffect(() => {
    setCurrencySymbol(getCurrencySymbol());
    const handleCurrencyChange = () => setCurrencySymbol(getCurrencySymbol());
    window.addEventListener('currencyChange', handleCurrencyChange);
    return () => window.removeEventListener('currencyChange', handleCurrencyChange);
  }, []);

  useEffect(() => {
    if (showCategoryModal && budgetInputRef.current) {
      setTimeout(() => budgetInputRef.current?.focus(), 100);
    }
  }, [showCategoryModal]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showAddMenu && !event.target.closest('.add-menu-container')) setShowAddMenu(false);
    };
    if (showAddMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showAddMenu]);

  useEffect(() => {
    const handleSimpleModeChange = () => {
      const saved = localStorage.getItem('simpleMode');
      setSimpleMode(saved === 'true');
    };
    window.addEventListener('simpleModeChange', handleSimpleModeChange);
    return () => window.removeEventListener('simpleModeChange', handleSimpleModeChange);
  }, []);

  useEffect(() => {
    const handlers = {
      openImportModal: () => setShowImportModal(true),
      openCopyModal: () => setShowCopyConfirm(true),
      openOnboarding: () => setShowOnboarding(true),
    };
    Object.entries(handlers).forEach(([evt, fn]) => window.addEventListener(evt, fn));
    return () => Object.entries(handlers).forEach(([evt, fn]) => window.removeEventListener(evt, fn));
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, POINTER_SENSOR_OPTIONS),
    useSensor(TouchSensor, TOUCH_SENSOR_OPTIONS)
  );

  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' ? window.innerWidth < 640 : false);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const resetTransactionForm = () => setTransactionForm({
    category_id: '',
    amount: '',
    date: getDefaultTransactionDate(),
    description: '',
    is_recurring: false,
  });

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <span className="text-xs font-mono uppercase tracking-widest text-foreground flex items-center gap-0.5">
        LOADING<span className="cursor-blink inline-block w-[7px] h-[13px] bg-foreground" />
      </span>
    </div>
  );

  if (isMobile && simpleMode) {
    return (
      <>
        <SimpleMobileMode
          categories={categories}
          categoryGroups={categoryGroups}
          lastUsedCategory={lastUsedCategory}
          monthlyIncome={monthlyIncome}
          totalSpent={totalSpent}
          transactions={transactions}
          allocations={allocations}
          onAddExpense={simpleModeExpense}
          onDeleteTransaction={deleteTransaction}
          onShowToast={(message, type) => setToast({ message, type })}
          onUpdateIncome={async (income) => {
            setMonthlyIncome(income);
            await supabase.from('monthly_income').upsert(
              { user_id: user.id, month: currentMonth, amount: income },
              { onConflict: 'user_id,month' }
            );
          }}
          currentMonth={currentMonth}
          pendingRecurring={pendingRecurring}
          onShowRecurring={() => setShowRecurringSheet(true)}
        />
        {showRecurringSheet && (
          <RecurringReviewSheet
            pending={pendingRecurring}
            categories={categories}
            onConfirm={confirmRecurring}
            onDeny={denyRecurring}
            onConfirmAll={confirmAllRecurring}
            onClose={() => setShowRecurringSheet(false)}
          />
        )}
        {pendingDelete && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" onClick={() => setPendingDelete(null)}>
            <div className="bg-background w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
              <h3 className="text-base font-semibold text-foreground mb-4">Delete transaction?</h3>
              {pendingDelete.isRecurring && (
                <label className="flex items-start gap-3 mb-5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={pendingDelete.stopRecurring}
                    onChange={e => setPendingDelete({ ...pendingDelete, stopRecurring: e.target.checked })}
                    className="h-4 w-4 mt-0.5 flex-shrink-0"
                  />
                  <span className="text-sm text-foreground">Also stop recurring (won't appear next month)</span>
                </label>
              )}
              <div className="flex gap-3">
                <Button onClick={confirmDelete} className="flex-1 h-10 bg-destructive hover:bg-destructive/90 text-[hsl(var(--destructive-foreground))]">Delete</Button>
                <Button variant="outline" onClick={() => setPendingDelete(null)} className="flex-1 h-10">Cancel</Button>
              </div>
            </div>
          </div>
        )}
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      </>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 pb-20 sm:pb-24 overflow-x-hidden w-full">
      {/* Budget Stats - Desktop Only */}
      <div className="hidden sm:block">
        {/* Budget Helper Message */}
        {monthlyIncome > 0 && (
          <>
            {totalBudgeted < monthlyIncome ? (
              <div className="mb-4 p-4 border-l-[3px] border-success bg-background">
                <p className="text-sm font-semibold uppercase tracking-widest text-success">
                  Assign {formatCurrency(monthlyIncome - totalBudgeted)} more to reach your income goal
                </p>
              </div>
            ) : totalBudgeted > monthlyIncome ? (
              <div className="mb-4 p-4 border-l-[3px] border-warning bg-background">
                <p className="text-sm font-semibold uppercase tracking-widest text-warning">
                  {formatCurrency(totalBudgeted - monthlyIncome)} over income
                </p>
              </div>
            ) : null}
          </>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 mb-6 border border-border">
          <div
            className="text-center p-5 bg-background cursor-pointer hover:bg-accent transition-colors border-r border-b border-border lg:border-b-0"
            onClick={() => setEditingIncome(true)}
          >
            <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-2">Monthly Income</p>
            {editingIncome ? (
              <NumberInput
                value={monthlyIncome}
                onChange={setMonthlyIncome}
                onBlur={() => saveMonthlyIncome(monthlyIncome, () => setEditingIncome(false))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveMonthlyIncome(monthlyIncome, () => setEditingIncome(false));
                  if (e.key === 'Escape') setEditingIncome(false);
                }}
                placeholder="Enter amount"
                className="text-left text-xl font-semibold h-12 w-full"
                hideButtons
                autoFocus
              />
            ) : (
              <p className="text-2xl font-medium font-mono tabular-nums text-foreground">{formatCurrency(monthlyIncome)}</p>
            )}
          </div>
          <div className="text-center p-5 bg-background border-b border-border lg:border-b-0 lg:border-r">
            <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-2">Budgeted</p>
            <p className="text-2xl font-medium font-mono tabular-nums text-foreground">{formatCurrency(totalBudgeted)}</p>
          </div>
          <div className="text-center p-5 bg-background border-r border-border">
            <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-2">Spent</p>
            <p className="text-2xl font-medium font-mono tabular-nums text-foreground">{formatCurrency(totalSpent)}</p>
          </div>
          <div className="text-center p-5 bg-background">
            <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-2">Available</p>
            <p className={cn("text-2xl font-medium font-mono tabular-nums", available < 0 ? "text-destructive" : "text-success")}>
              {formatCurrency(available)}
            </p>
          </div>
        </div>

      </div>

      {/* Action Buttons - all screen sizes */}
      <div className="flex items-center gap-2 sm:gap-3 flex-wrap justify-between sm:justify-start px-4 sm:px-0">
        <div className="relative add-menu-container">
          <Button
            onClick={() => setShowAddMenu(!showAddMenu)}
            className="px-4 sm:px-6 h-11 font-medium touch-manipulation"
          >
            <Plus className="h-4 w-4 mr-1 sm:mr-2" strokeLinecap="square" strokeLinejoin="miter" />
            <span>Add</span>
            <ChevronDown className="h-4 w-4 ml-1 sm:ml-2" />
          </Button>
          {showAddMenu && (
            <div className="absolute top-full mt-2 bg-background border border-border min-w-[200px] z-50 overflow-hidden divide-y divide-border">
              <button
                onClick={() => { setShowCategoryModal(true); setShowAddMenu(false); }}
                className="w-full text-left px-4 py-3 text-sm text-foreground hover:bg-accent transition-colors"
              >
                Add Category
              </button>
              <button
                onClick={() => { setShowGroupModal(true); setShowAddMenu(false); }}
                className="w-full text-left px-4 py-3 text-sm text-foreground hover:bg-accent transition-colors"
              >
                Add Group
              </button>
            </div>
          )}
        </div>

        {pendingRecurring.length > 0 && !showRecurringSheet ? (
          <button
            onClick={() => setShowRecurringSheet(true)}
            className="ml-auto sm:ml-0 flex items-center gap-2 px-3 h-11 bg-warning text-warning-foreground text-sm font-medium hover:opacity-90 transition-opacity whitespace-nowrap"
          >
            <RefreshCw className="h-4 w-4 flex-shrink-0" />
            {pendingRecurring.length} recurring to review
          </button>
        ) : (
          <div className="flex-1 sm:hidden" />
        )}

        <Button
          variant="outline"
          onClick={() => setShowImportModal(true)}
          className="hidden sm:flex px-4 sm:px-6 h-11 font-medium touch-manipulation"
        >
          <Upload className="h-4 w-4 mr-2" />
          <span>Import CSV</span>
        </Button>

        <Button
          variant="outline"
          onClick={() => setShowCopyConfirm(true)}
          className="hidden sm:flex px-4 sm:px-6 h-11 font-medium touch-manipulation"
        >
          <Copy className="h-4 w-4 mr-2" />
          <span>Copy Last Month</span>
        </Button>
      </div>

      {/* Desktop: BudgetTable */}
      <div className="hidden lg:block">
        <BudgetTable
          categoryGroups={categoryGroups}
          categories={categories}
          allocations={allocations}
          transactions={transactions}
          expandedGroups={expandedGroups}
          onToggleGroup={toggleGroup}
          sensors={sensors}
          onDragEnd={handleDragEnd}
          editingBudget={editingBudget}
          setEditingBudget={setEditingBudget}
          onUpdateBudget={updateBudgetInline}
          onAddTransaction={(cat) => {
            setTransactionForm(f => ({ ...f, category_id: cat.id, date: getDefaultTransactionDate() }));
            setShowAddTransaction(true);
          }}
          onEditCategory={(cat) => {
            setCategoryForm({ name: cat.name, budgeted_amount: String(allocations.get(cat.id) || 0), group_id: cat.group_id || '' });
            setShowCategoryModal(true);
          }}
          onEditGroup={(group) => setEditingGroup({ id: group.id, name: group.name })}
          onViewTransactions={(cat) => { setSelectedCategoryForSheet(cat); setShowCategorySheet(true); }}
          onAddCategory={(group) => {
            setCategoryForm(f => ({ ...f, group_id: group.id }));
            setShowCategoryModal(true);
          }}
        />
      </div>

      {/* Mobile stat strip */}
      <div className="grid grid-cols-3 sm:hidden border border-border divide-x divide-border">
        <button
          onClick={() => setEditingIncome(true)}
          className="text-center px-3 py-3 active:bg-accent transition-colors"
        >
          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1">Income</p>
          {editingIncome ? (
            <NumberInput
              value={monthlyIncome}
              onChange={setMonthlyIncome}
              onBlur={() => saveMonthlyIncome(monthlyIncome, () => setEditingIncome(false))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveMonthlyIncome(monthlyIncome, () => setEditingIncome(false));
                if (e.key === 'Escape') setEditingIncome(false);
              }}
              hideButtons
              autoFocus
              className="h-6 text-sm font-semibold w-full text-center"
            />
          ) : (
            <p className="text-base font-medium font-mono tabular-nums text-foreground underline decoration-dotted underline-offset-2">{formatCurrency(monthlyIncome)}</p>
          )}
        </button>
        <div className="text-center px-3 py-3">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1">Budgeted</p>
          <p className="text-base font-medium font-mono tabular-nums text-foreground">{formatCurrency(totalBudgeted)}</p>
        </div>
        <div className="text-center px-3 py-3">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1">Available</p>
          <p className={cn("text-base font-medium font-mono tabular-nums", available < 0 ? "text-destructive" : "text-success")}>{formatCurrency(available)}</p>
        </div>
      </div>

      {/* Mobile/Tablet: DnD category cards */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <div className="lg:hidden space-y-4">
          {categoryGroups.map((group) => {
            const groupCategories = categories
              .filter(c => c.group_id === group.id)
              .sort((a, b) => a.sort_order - b.sort_order);
            const groupBudget = groupCategories.reduce((sum, c) => sum + (allocations.get(c.id) || 0), 0);
            const groupSpent = groupCategories.reduce((sum, c) => {
              return sum + (transactionsByCategory[c.id] || []).filter(t => t.amount < 0).reduce((s, t) => s + Math.abs(t.amount), 0);
            }, 0);
            const groupProgress = groupBudget > 0 ? (groupSpent / groupBudget) * 100 : 0;
            const progressColor = getProgressColor(groupProgress);
            const isExpanded = expandedGroups.has(group.id);

            return (
              <div key={group.id} className="border border-border bg-background overflow-hidden">
                <div className="relative flex items-center px-4 py-3 gap-2 cursor-pointer" onClick={() => toggleGroup(group.id)}>
                  {/* Progress Glow Indicator */}
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-background overflow-hidden">
                    <div
                      className="h-full transition-all duration-500 ease-out"
                      style={{ width: `${Math.min(groupProgress, 100)}%`, backgroundColor: progressColor, boxShadow: `0 0 8px ${progressColor}` }}
                    />
                  </div>
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleGroup(group.id); }}
                      className="flex-shrink-0 hover:opacity-80 transition-opacity p-1 -m-1"
                      aria-label="Toggle group"
                    >
                      <ChevronDown className={cn("h-5 w-5 text-muted-foreground transition-transform", !isExpanded && "-rotate-90")} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setEditingGroup({ id: group.id, name: group.name }); }}
                      className="flex-1 text-left min-w-0 hover:opacity-80 transition-opacity"
                    >
                      <h3 className="text-xl font-medium text-foreground truncate">{group.name}</h3>
                      <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                        {formatCurrency(groupSpent)} of {formatCurrency(groupBudget)}
                      </p>
                    </button>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); deleteGroup(group.id); }}
                    className="hidden sm:flex text-muted-foreground hover:text-destructive h-9 w-9"
                    aria-label="Delete group"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                {isExpanded && (
                  <div className="bg-background">
                    <SortableContext items={groupCategories.map(c => c.id)} strategy={verticalListSortingStrategy}>
                      {groupCategories.map((category) => {
                        const budgeted = allocations.get(category.id) || 0;
                        const catTxns = (transactionsByCategory[category.id] || []).filter(t => t.amount < 0);
                        const spent = catTxns.reduce((sum, t) => sum + Math.abs(t.amount), 0);
                        const remaining = budgeted - spent;
                        const progressPercent = budgeted > 0 ? (spent / budgeted) * 100 : 0;

                        return (
                          <SortableCategoryRow
                            key={category.id}
                            category={category}
                            onClick={() => { setSelectedCategoryForSheet(category); setShowCategorySheet(true); }}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2 flex-1">
                                <button
                                  onClick={(e) => { e.stopPropagation(); setSelectedCategoryForSheet(category); setShowCategorySheet(true); }}
                                  className="text-base font-medium text-foreground hover:opacity-70 transition-opacity text-left"
                                >
                                  {category.name}
                                </button>
                              </div>
                              <div className="text-right">
                                <p className="text-base font-medium text-foreground font-mono tabular-nums">
                                  {formatCurrency(remaining)} left
                                </p>
                              </div>
                            </div>
                            <div className="relative h-2 bg-background overflow-hidden">
                              <div
                                className={cn(
                                  "absolute top-0 left-0 h-full transition-all duration-300",
                                  progressPercent > 100 ? "bg-destructive" : progressPercent >= 100 ? "bg-success" : "bg-warning"
                                )}
                                style={{ width: `${Math.min(progressPercent, 100)}%` }}
                              />
                            </div>
                            <div className="mt-1 text-xs text-muted-foreground">
                              {formatCurrency(spent)} of {formatCurrency(budgeted)}
                            </div>
                          </SortableCategoryRow>
                        );
                      })}
                    </SortableContext>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </DndContext>

      {/* Floating Action Button - mobile/tablet */}
      <div className="fixed bottom-4 right-4 lg:hidden z-30">
        <button
          className="btn-gradient-fab h-14 w-14 shadow-lg hover:shadow-xl transition-shadow touch-manipulation flex items-center justify-center"
          onClick={() => {
            setSelectedCategory(null);
            setTransactionForm(f => ({ ...f, category_id: '', date: getDefaultTransactionDate() }));
            setShowAddTransaction(true);
          }}
          aria-label="Add transaction"
        >
          <Plus className="h-6 w-6 text-primary-foreground relative z-10" strokeLinecap="square" strokeLinejoin="miter" />
        </button>
      </div>

      {/* Recurring Review Sheet */}
      {showRecurringSheet && (
        <RecurringReviewSheet
          pending={pendingRecurring}
          categories={categories}
          onConfirm={confirmRecurring}
          onDeny={denyRecurring}
          onConfirmAll={confirmAllRecurring}
          onClose={() => setShowRecurringSheet(false)}
        />
      )}

      {/* Delete Confirmation Modal */}
      {pendingDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" onClick={() => setPendingDelete(null)}>
          <div className="bg-background w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-foreground mb-4">Delete transaction?</h3>
            {pendingDelete.isRecurring && (
              <label className="flex items-start gap-3 mb-5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={pendingDelete.stopRecurring}
                  onChange={e => setPendingDelete({ ...pendingDelete, stopRecurring: e.target.checked })}
                  className="h-4 w-4 mt-0.5 flex-shrink-0"
                />
                <span className="text-sm text-foreground">Also stop recurring (won't appear next month)</span>
              </label>
            )}
            <div className="flex gap-3">
              <Button onClick={confirmDelete} className="flex-1 h-10 bg-destructive hover:bg-destructive/90 text-[hsl(var(--destructive-foreground))]">
                Delete
              </Button>
              <Button variant="outline" onClick={() => setPendingDelete(null)} className="flex-1 h-10">
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Add Transaction Modal */}
      {showAddTransaction && (
        <div
          className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4"
          onClick={() => setShowAddTransaction(false)}
        >
          <div
            className="bg-background w-full sm:max-w-md max-h-[92vh] flex flex-col overflow-hidden border-t border-border sm:border"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0">
              <span className="text-[11px] font-semibold uppercase tracking-widest text-foreground">Add Transaction</span>
              <button type="button" onClick={() => setShowAddTransaction(false)} className="text-muted-foreground hover:text-foreground transition-colors p-0.5">
                <X className="h-4 w-4" />
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                addTransaction(transactionForm, () => {
                  resetTransactionForm();
                  setShowAddTransaction(false);
                  setSelectedCategory(null);
                });
              }}
              className="flex-1 overflow-y-auto flex flex-col divide-y divide-border"
            >
              {/* Category */}
              <div className="px-5 py-3">
                <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground block mb-1.5">Category</label>
                <div className="relative">
                  <select
                    value={transactionForm.category_id}
                    onChange={(e) => setTransactionForm({ ...transactionForm, category_id: e.target.value })}
                    className="w-full h-11 bg-transparent text-sm text-foreground focus:outline-none appearance-none pr-6 cursor-pointer"
                  >
                    <option value="">No category</option>
                    {categoryGroups.map(group => (
                      <optgroup key={group.id} label={group.name}>
                        {categories.filter(c => c.group_id === group.id).map(cat => {
                          const catTxns = (transactionsByCategory[cat.id] || []).filter(t => t.amount < 0);
                          const catSpent = catTxns.reduce((sum, t) => sum + Math.abs(t.amount), 0);
                          const catBudget = allocations.get(cat.id) || 0;
                          const catRemaining = catBudget - catSpent;
                          const remainingLabel = catRemaining >= 0
                            ? `${formatCurrency(catRemaining)} left`
                            : `${formatCurrency(Math.abs(catRemaining))} over`;
                          return <option key={cat.id} value={cat.id}>{cat.name} — {remainingLabel}</option>;
                        })}
                      </optgroup>
                    ))}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                </div>
              </div>

              {/* Amount */}
              <div className="px-5 py-3">
                <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground block mb-1.5">Amount</label>
                <div className="flex items-center gap-3 h-11">
                  <span className="text-sm font-mono text-muted-foreground flex-shrink-0">{currencySymbol}</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={transactionForm.amount}
                    onChange={(e) => setTransactionForm({ ...transactionForm, amount: e.target.value })}
                    required
                    placeholder="0.00"
                    className="flex-1 h-full bg-transparent text-sm font-mono text-foreground placeholder:text-muted-foreground/40 focus:outline-none"
                  />
                </div>
              </div>

              {/* Date */}
              <div className="px-5 py-3">
                <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground block mb-1.5">Date</label>
                <input
                  type="date"
                  value={transactionForm.date}
                  onChange={(e) => setTransactionForm({ ...transactionForm, date: e.target.value })}
                  required
                  className="w-full h-11 bg-transparent text-sm font-mono text-foreground focus:outline-none"
                />
              </div>

              {/* Recurring */}
              <div className="px-5 py-3 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground block mb-0.5">Recurring</span>
                  <span className="text-sm text-foreground">
                    Repeats on day {parseInt(transactionForm.date.split('-')[2], 10)} each month
                  </span>
                </div>
                <label className="relative flex-shrink-0 cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={transactionForm.is_recurring || false}
                    onChange={(e) => setTransactionForm({ ...transactionForm, is_recurring: e.target.checked })}
                  />
                  <div className="w-11 h-6 border border-border bg-background peer-checked:bg-primary peer-checked:border-primary transition-all duration-200" />
                  <div className="absolute top-1 left-1 w-4 h-4 border border-foreground bg-transparent peer-checked:translate-x-5 peer-checked:bg-background peer-checked:border-primary transition-all duration-200" />
                </label>
              </div>

              {/* Description */}
              <div className="px-5 py-3">
                <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground block mb-1.5">
                  Description <span className="normal-case tracking-normal font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={transactionForm.description}
                  onChange={(e) => setTransactionForm({ ...transactionForm, description: e.target.value })}
                  placeholder="Store name, notes, etc."
                  className="w-full h-11 bg-transparent text-sm text-foreground placeholder:text-muted-foreground/40 focus:outline-none"
                />
              </div>

              {/* Submit */}
              <div className="px-5 py-4 flex-shrink-0 mt-auto">
                <button
                  type="submit"
                  className="w-full h-12 bg-primary text-primary-foreground text-[11px] font-semibold uppercase tracking-widest hover:bg-primary/90 active:scale-[0.99] transition-all touch-manipulation"
                >
                  Add Transaction
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Category Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" onClick={() => setShowCategoryModal(false)}>
          <div className="bg-background w-full sm:max-w-md shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 sm:p-6 border-b border-border sticky top-0 bg-background z-10">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold text-foreground">Add Category</h3>
                <Button variant="ghost" size="icon" onClick={() => setShowCategoryModal(false)} className="h-9 w-9">
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                addCategory(categoryForm, () => {
                  setCategoryForm({ name: '', budgeted_amount: '', group_id: '' });
                  setShowCategoryModal(false);
                });
              }}
              className="p-4 sm:p-6 space-y-4"
            >
              <div>
                <Label className="mb-2 block">Quick Templates</Label>
                <div className="flex flex-wrap gap-2 mb-4">
                  {categoryTemplates.map((template) => (
                    <button
                      key={template.name}
                      type="button"
                      onClick={() => setCategoryForm(f => ({ ...f, name: template.name, budgeted_amount: String(template.budget) }))}
                      className="px-3 py-1.5 text-xs border border-border hover:bg-accent transition-colors"
                    >
                      {template.name}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Category Name</Label>
                <Input
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                  placeholder="e.g., Shopping"
                  required
                  className="mt-1 h-11"
                />
              </div>
              <div>
                <Label>Group <span className="text-destructive">*</span></Label>
                <select
                  value={categoryForm.group_id}
                  onChange={(e) => setCategoryForm({ ...categoryForm, group_id: e.target.value })}
                  className="flex h-11 w-full border border-border bg-input px-4 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-ring"
                  required
                >
                  <option value="">Select a group</option>
                  {categoryGroups.map(group => (
                    <option key={group.id} value={group.id}>{group.name}</option>
                  ))}
                </select>
                {categoryGroups.length === 0 && (
                  <p className="text-xs text-muted-foreground mt-1">Create a group first</p>
                )}
              </div>
              <div>
                <Label>Monthly Budget</Label>
                <NumberInput
                  ref={budgetInputRef}
                  value={categoryForm.budgeted_amount}
                  onChange={(value) => setCategoryForm({ ...categoryForm, budgeted_amount: value })}
                  placeholder="Enter amount"
                  className="mt-1 h-11"
                  min={0}
                />
              </div>
              <Button type="submit" className="w-full h-11 text-base font-medium mt-6 bg-primary hover:bg-primary/90 touch-manipulation">
                Add Category
              </Button>
            </form>
          </div>
        </div>
      )}

      {/* Add Group Modal */}
      {showGroupModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" onClick={() => setShowGroupModal(false)}>
          <div className="bg-background w-full sm:max-w-md shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 sm:p-6 border-b border-border sticky top-0 bg-background z-10">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold text-foreground">Add Group</h3>
                <Button variant="ghost" size="icon" onClick={() => setShowGroupModal(false)} className="h-9 w-9">
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                addGroup(groupForm, () => {
                  setGroupForm({ name: '' });
                  setShowGroupModal(false);
                });
              }}
              className="p-4 sm:p-6 space-y-4"
            >
              <div>
                <Label>Group Name</Label>
                <Input
                  value={groupForm.name}
                  onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
                  placeholder="e.g., Personal Expenses"
                  required
                  className="mt-1 h-11"
                />
              </div>
              <Button type="submit" className="w-full h-11 text-base font-medium mt-6 bg-primary hover:bg-primary/90 touch-manipulation">
                Add Group
              </Button>
            </form>
          </div>
        </div>
      )}

      {/* Rename Group Modal */}
      {editingGroup && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" onClick={() => setEditingGroup(null)}>
          <div className="bg-background w-full sm:max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 sm:p-6 border-b border-border sticky top-0 bg-background z-10">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold text-foreground">Rename Group</h3>
                <Button variant="ghost" size="icon" onClick={() => setEditingGroup(null)} className="h-9 w-9">
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                renameGroup(editingGroup.id, editingGroup.name, () => setEditingGroup(null));
              }}
              className="p-4 sm:p-6 space-y-4"
            >
              <div>
                <Label>Group Name</Label>
                <Input
                  value={editingGroup.name}
                  onChange={(e) => setEditingGroup({ ...editingGroup, name: e.target.value })}
                  placeholder="e.g., Personal Expenses"
                  required
                  autoFocus
                  className="mt-1 h-11"
                />
              </div>
              <div className="flex gap-3 mt-6">
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => { deleteGroup(editingGroup.id, () => setEditingGroup(null)); }}
                  className="h-11 px-4 touch-manipulation"
                >
                  Delete
                </Button>
                <Button type="submit" className="flex-1 h-11 text-base font-medium bg-primary hover:bg-primary/90 touch-manipulation">
                  Save
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Import CSV Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" onClick={() => setShowImportModal(false)}>
          <div className="bg-background w-full sm:max-w-md shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 sm:p-6 border-b border-border sticky top-0 bg-background z-10">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold text-foreground">Import CSV</h3>
                <Button variant="ghost" size="icon" onClick={() => setShowImportModal(false)} className="h-9 w-9">
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              <p className="text-sm text-muted-foreground mb-4">
                Upload a CSV file. The file should include columns for date (YYYY-MM-DD) and amount. Description fields are optional.
              </p>
              <div>
                <Label>Category (optional)</Label>
                <select
                  value={selectedCategory?.id || ''}
                  onChange={(e) => {
                    const cat = categories.find(c => c.id === e.target.value);
                    setSelectedCategory(cat || null);
                  }}
                  className="flex h-11 w-full border border-border bg-input px-4 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">Select category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>CSV File</Label>
                <label className="mt-1 flex items-center gap-3 cursor-pointer">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={(e) => importCSV(e.target.files[0], selectedCategory, () => setShowImportModal(false))}
                    className="sr-only"
                    id="csv-file-input"
                  />
                  <span className="inline-flex items-center gap-2 px-4 h-11 border border-input bg-background text-sm font-medium text-foreground hover:bg-accent transition-colors cursor-pointer">
                    <Upload className="h-4 w-4" />
                    Choose file
                  </span>
                </label>
                <p className="text-xs text-muted-foreground mt-2">
                  Expected format: date (YYYY-MM-DD), amount, and optionally description/payee/memo/notes
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Copy Last Month Modal */}
      {showCopyConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" onClick={() => setShowCopyConfirm(false)}>
          <div className="bg-background w-full sm:max-w-md shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 sm:p-6 border-b border-border sticky top-0 bg-background z-10">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold text-foreground">Copy Last Month</h3>
                <Button variant="ghost" size="icon" onClick={() => setShowCopyConfirm(false)} className="h-9 w-9">
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              <p className="text-sm text-muted-foreground">
                This will copy all budget allocations and income from the previous month. Any existing budgets for this month will be overwritten.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button variant="outline" onClick={() => setShowCopyConfirm(false)} className="flex-1 h-11 touch-manipulation">
                  Cancel
                </Button>
                <Button
                  onClick={() => copyPreviousMonth(() => setShowCopyConfirm(false))}
                  className="flex-1 h-11 bg-primary hover:bg-primary/90 touch-manipulation"
                >
                  Copy
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Onboarding Modal */}
      <OnboardingModal
        isOpen={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        isDemo={true}
        onComplete={async (_groups, _categories, incomeValue) => {
          const parsed = parseFloat(incomeValue) || 0;
          if (parsed > 0) {
            await supabase.from('monthly_income').upsert({
              user_id: user.id,
              month: currentMonth,
              amount: parsed,
            }, { onConflict: 'user_id,month' });
            setMonthlyIncome(parsed);
          }
          setShowOnboarding(false);
        }}
      />

      {/* Category Bottom Sheet */}
      {showCategorySheet && selectedCategoryForSheet && (
        <CategoryBottomSheet
          category={selectedCategoryForSheet}
          budgeted={allocations.get(selectedCategoryForSheet.id) || 0}
          spent={(transactionsByCategory[selectedCategoryForSheet.id] || [])
            .filter(t => t.amount < 0)
            .reduce((sum, t) => sum + Math.abs(t.amount), 0)}
          remaining={
            (allocations.get(selectedCategoryForSheet.id) || 0) -
            (transactionsByCategory[selectedCategoryForSheet.id] || [])
              .filter(t => t.amount < 0)
              .reduce((sum, t) => sum + Math.abs(t.amount), 0)
          }
          transactions={(transactionsByCategory[selectedCategoryForSheet.id] || [])
            .filter(t => t.amount < 0)
            .sort((a, b) => new Date(b.date) - new Date(a.date))}
          onClose={() => { setShowCategorySheet(false); setSelectedCategoryForSheet(null); }}
          onAddExpense={() => {
            setShowCategorySheet(false);
            setTransactionForm(f => ({ ...f, category_id: selectedCategoryForSheet.id, date: getDefaultTransactionDate() }));
            setShowAddTransaction(true);
          }}
          onDeleteTransaction={deleteTransaction}
          onUpdateBudget={updateBudgetInline}
          onRenameCategory={renameCategory}
          onDeleteCategory={(id) => deleteCategory(id, () => { setShowCategorySheet(false); setSelectedCategoryForSheet(null); })}
        />
      )}
    </div>
  );
};

export default Budget;
